import styled from 'styled-components';

export const PanelContainer = styled.div`
  min-height: 100vh;
  background: var(--bg-section);

  .content {
    max-width: 1100px;
    margin: 0 auto;
    padding: 32px 20px 72px;
  }
`;

export const PanelHeader = styled.header`
  background: var(--bg-card);
  border-bottom: 1px solid var(--border-light);
  box-shadow: var(--shadow-xs);
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 24px;
  height: 64px;

  .brand {
    display: flex;
    align-items: center;
    gap: 10px;

    img { height: 20px; width: auto; max-width: 100%; }

    .admin-chip {
      background: var(--primary);
      color: var(--white);
      font-size: var(--text-xs);
      font-weight: 700;
      letter-spacing: 1px;
      text-transform: uppercase;
      padding: 3px 10px;
      border-radius: var(--radius-round);
    }
  }

  .user {
    display: flex;
    align-items: center;
    gap: 12px;

    span {
      font-size: var(--text-sm);
      color: var(--text-secondary);
      font-weight: 500;
    }
  }

  @media (max-width: 480px) {
    padding: 0 16px;
    .brand { gap: 8px; min-width: 0; }
    .user span { display: none; }
  }
`;

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;
  margin-bottom: 28px;

  @media (max-width: 480px) { grid-template-columns: 1fr 1fr; }
`;

export const StatBox = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  border-top: 3px solid ${props => props.$accent || 'var(--primary)'};
  padding: 18px 20px;
  box-shadow: var(--shadow-xs);

  strong {
    display: block;
    font-size: var(--text-2xl);
    font-weight: 700;
    line-height: 1.1;
  }

  span {
    font-size: var(--text-xs);
    color: var(--text-secondary);
  }
`;

export const SectionTabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0 8px;
  border-bottom: 2px solid var(--border-light);
  margin-bottom: 20px;
`;

export const SectionTab = styled.button`
  padding: 12px 20px;
  border: none;
  background: none;
  font-family: var(--font-brand);
  font-size: var(--text-base);
  font-weight: ${props => (props.$active ? 700 : 500)};
  color: ${props => (props.$active ? 'var(--primary)' : 'var(--text-secondary)')};
  border-bottom: 3px solid ${props => (props.$active ? 'var(--primary)' : 'transparent')};
  margin-bottom: -2px;
  cursor: pointer;
  transition: color var(--transition-fast), border-color var(--transition-fast);

  &:hover { color: var(--primary); }

  @media (max-width: 520px) {
    padding: 10px 12px;
    font-size: var(--text-sm);
  }
`;

export const DataTable = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-xs);
  overflow-x: auto;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-sm);
    min-width: 760px;
  }

  th {
    text-align: left;
    padding: 12px 16px;
    font-size: var(--text-xs);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--text-muted);
    border-bottom: 1px solid var(--border-light);
    background: var(--bg-section);
    white-space: nowrap;
  }

  td {
    padding: 12px 16px;
    border-bottom: 1px solid var(--border-light);
    color: var(--text-secondary);
    vertical-align: top;

    strong { color: var(--text-primary); }
  }

  tbody tr:last-child td { border-bottom: none; }

  tbody tr {
    transition: background var(--transition-fast);
    &:hover { background: var(--bg-section); }
  }

  .sub {
    font-size: var(--text-xs);
    color: var(--text-muted);
    margin-top: 2px;

    &.warn { color: var(--warning-dark); }
    &.mono { font-family: monospace; }
  }
`;

export const TabRow = styled.div`
  display: inline-flex;
  gap: 6px;
  background: var(--bg-input);
  border-radius: var(--radius-round);
  padding: 5px;
  margin-bottom: 24px;
`;

export const Tab = styled.button`
  padding: 9px 22px;
  border: none;
  border-radius: var(--radius-round);
  background: ${props => (props.$active ? 'var(--bg-card)' : 'transparent')};
  color: ${props => (props.$active ? 'var(--primary)' : 'var(--text-secondary)')};
  font-family: var(--font-brand);
  font-size: var(--text-sm);
  font-weight: ${props => (props.$active ? 700 : 500)};
  box-shadow: ${props => (props.$active ? 'var(--shadow-xs)' : 'none')};
  cursor: pointer;
  transition: all var(--transition-fast);
`;

export const EmpresaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const EmpresaCard = styled.article`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 20px;
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 22px 24px;
  box-shadow: var(--shadow-xs);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const EmpresaMain = styled.div`
  min-width: 0;

  .title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    margin-bottom: 10px;

    h3 { font-size: var(--text-lg); }
  }

  .description {
    font-size: var(--text-sm);
    color: var(--text-secondary);
    margin: 10px 0;
    line-height: 1.5;
  }
`;

export const EmpresaMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;

  span {
    font-size: var(--text-xs);
    color: var(--text-secondary);
  }
`;

export const DocList = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border-light);

  strong {
    font-size: var(--text-xs);
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  a {
    font-size: var(--text-xs);
    font-weight: 600;
    background: var(--primary-subtle);
    color: var(--primary);
    padding: 4px 12px;
    border-radius: var(--radius-round);

    &:hover { background: var(--bg-section); }
  }

  .no-docs {
    font-size: var(--text-xs);
    color: var(--warning-dark);
    font-weight: 600;
  }
`;

export const CardActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;

  @media (max-width: 720px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 64px 20px;
  background: var(--bg-card);
  border: 1px dashed var(--border-medium);
  border-radius: var(--radius-lg);

  h3 { margin-bottom: 8px; }

  p {
    color: var(--text-secondary);
    font-size: var(--text-sm);
  }
`;
